import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * DetectTodosModal · C3 — IA detecta TODOs/FALTA/REVISAR en el guion.
 *
 * Flujo:
 *  1. Llama a `POST /api/icaa/projects/{pid}/ai/script/detect-todos`.
 *  2. Muestra la lista propuesta con checkbox por cada item.
 *  3. Al confirmar, crea las tareas seleccionadas vía `POST /api/inbox/tasks`.
 *
 * No toca el editor de guion — vive como modal del proyecto invocable desde
 * el panel "Notas / Tareas" (NotesTab).
 */
export default function DetectTodosModal({ projectId, projectTitle, onClose, onCreated }) {
  const [phase, setPhase] = useState("loading"); // loading | review | creating | done | error
  const [todos, setTodos] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [error, setError] = useState("");
  const [priority, setPriority] = useState("med");
  const [creating, setCreating] = useState(0);

  // Run detection on mount
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.post(`/icaa/projects/${projectId}/ai/script/detect-todos`);
        if (cancel) return;
        const arr = r.data?.todos || [];
        setTodos(arr);
        setSelected(new Set(arr.map((_, i) => i)));
        setPhase("review");
      } catch (e) {
        if (cancel) return;
        setError(e?.response?.data?.detail || e.message || "Error analizando el guion.");
        setPhase("error");
      }
    })();
    return () => { cancel = true; };
  }, [projectId]);

  const toggle = (i) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const confirm = async () => {
    const chosen = Array.from(selected).map((i) => todos[i]);
    if (chosen.length === 0) { onClose(); return; }
    setPhase("creating");
    let ok = 0;
    for (const t of chosen) {
      try {
        await api.post("/inbox/tasks", {
          project_id: projectId,
          text: t.text,
          priority,
        });
        ok += 1;
        setCreating(ok);
      } catch (_e) { /* ignore individual fails */ }
    }
    onCreated?.(ok);
    setPhase("done");
    setTimeout(() => onClose(), 1200);
  };

  return (
    <div className="fixed inset-0 z-[65] flex items-center justify-center bg-gb-black/70 p-4 backdrop-blur"
         onClick={onClose} data-testid="detect-todos-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-7 shadow-2xl">
        <p className="gb-eyebrow">IA · pendientes del guion</p>
        <h2 className="mt-1 gb-display text-2xl">Detectar tareas en el guion</h2>
        <p className="mt-2 text-sm text-gb-muted">
          La IA lee el guion de <strong>{projectTitle}</strong> y propone tareas a partir de marcas TODO / FALTA / REVISAR / PENDIENTE que has dejado en el texto.
        </p>

        {phase === "loading" && (
          <div className="mt-8 flex flex-col items-center gap-3 py-8" data-testid="detect-todos-loading">
            <Spinner />
            <p className="text-sm text-gb-muted">Analizando el guion…<br/>Puede tardar 30-60 segundos.</p>
          </div>
        )}

        {phase === "error" && (
          <div className="mt-6 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="detect-todos-error">
            {error}
          </div>
        )}

        {phase === "review" && todos.length === 0 && (
          <div className="mt-6 rounded-md border border-dashed border-gb-paper bg-gb-paper-soft/40 p-6 text-center" data-testid="detect-todos-empty">
            <p className="font-serif text-lg">La IA no encontró marcas pendientes</p>
            <p className="mt-2 text-sm text-gb-muted">
              Tu guion no tiene marcas TODO / FALTA / REVISAR detectables. Si quieres usar esta función, escribe estas marcas dentro del texto del guion donde tengas algo que recordar.
            </p>
          </div>
        )}

        {phase === "review" && todos.length > 0 && (
          <>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-md bg-gb-paper-soft p-3">
              <p className="text-[11px] uppercase tracking-[0.22em] text-gb-black/75">
                {todos.length} marca{todos.length === 1 ? "" : "s"} detectada{todos.length === 1 ? "" : "s"} · {selected.size} seleccionada{selected.size === 1 ? "" : "s"}
              </p>
              <div className="flex items-center gap-2 text-[11px]">
                <span className="uppercase tracking-widest text-gb-muted">Prioridad:</span>
                <select value={priority} onChange={(e) => setPriority(e.target.value)}
                        data-testid="detect-todos-priority"
                        className="rounded-md border border-gb-paper bg-white px-2 py-1 text-xs outline-none focus:border-gb-red">
                  <option value="low">Baja</option>
                  <option value="med">Media</option>
                  <option value="high">Alta</option>
                </select>
              </div>
            </div>

            <ul className="mt-4 space-y-2" data-testid="detect-todos-list">
              {todos.map((t, i) => (
                <li key={i}
                    className={`group cursor-pointer rounded-md border p-3 transition ${
                      selected.has(i)
                        ? "border-gb-red/40 bg-gb-red/5"
                        : "border-gb-paper bg-gb-surface hover:border-gb-red/30"
                    }`}
                    onClick={() => toggle(i)}
                    data-testid={`detect-todo-${i}`}>
                  <div className="flex items-start gap-3">
                    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition ${
                      selected.has(i) ? "border-gb-red bg-gb-red text-white" : "border-gb-paper"
                    }`}>
                      {selected.has(i) && <span className="text-[11px] leading-none">✓</span>}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gb-black">{t.text}</p>
                      {t.context && (
                        <p className="mt-1 truncate text-[11px] italic text-gb-muted">
                          "{t.context}"
                        </p>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

        {phase === "creating" && (
          <div className="mt-8 flex flex-col items-center gap-3 py-8" data-testid="detect-todos-creating">
            <Spinner />
            <p className="text-sm text-gb-muted">Creando tareas… ({creating} / {selected.size})</p>
          </div>
        )}

        {phase === "done" && (
          <div className="mt-6 rounded-md bg-emerald-50 px-4 py-3 text-center text-sm text-emerald-800" data-testid="detect-todos-done">
            ✓ Creadas {creating} tarea{creating === 1 ? "" : "s"}.
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose}
                  className="rounded-full border border-gb-paper px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-gb-muted hover:bg-gb-paper-soft">
            {phase === "done" ? "Cerrar" : "Cancelar"}
          </button>
          {phase === "review" && todos.length > 0 && (
            <button onClick={confirm} disabled={selected.size === 0}
                    data-testid="detect-todos-submit"
                    className="rounded-full bg-gb-red px-6 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition hover-gb-red disabled:opacity-40">
              Crear {selected.size} tarea{selected.size === 1 ? "" : "s"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const Spinner = () => (
  <svg className="h-8 w-8 animate-spin text-gb-red" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="40 60" />
  </svg>
);
